import { useRef, useState } from 'react'
import { Link } from 'react-router'
import { Avatar } from '../../components/Avatar'
import { BotTag } from '../../components/BotTag'
import { Icon } from '../../components/Icon'
import type { LocalChannel } from '../../db/db'
import { useSheetTriggers, type SheetRequest } from '../../hooks/gestures/useSheetTriggers'
import { useIsOnline } from '../../hooks/useOnline'
import { dmPartnerId, lastActivity, previewText, type UserMap } from '../../lib/channels'
import { formatRelativeTime } from '../../lib/format'
import { managedInfo } from '../../lib/managed'
import { isRestricted } from '../../lib/posting'
import { quietLabel, quietState } from '../channels/quiet'
import { useSession } from '../../state/session'
import { ConversationActions } from './ConversationActions'

interface Props {
  channel: LocalChannel
  name: string
  users: UserMap
  active: boolean
}

export function ConversationRow({ channel, name, users, active }: Props) {
  const meId = useSession((s) => s.me?.id ?? null)
  const partnerId = dmPartnerId(channel, meId, users)
  const bot = channel.type === 'bot_dm'
  const online = useIsOnline(bot ? null : partnerId)
  const quiet = quietState(channel)
  const quietText = quietLabel(quiet, 'short')
  const typeLabel =
    channel.type === 'channel' ? 'channel' : channel.type === 'dm' ? 'direct message' : channel.type === 'bot_dm' ? 'message from a bot' : 'group message'
  const managed = managedInfo(channel)
  const restricted = isRestricted(channel)
  const linkRef = useRef<HTMLAnchorElement>(null)
  const [actions, setActions] = useState<SheetRequest | null>(null)
  // Hold the row (or right-click it, or the Menu key) for its quick actions (spec §3.6).
  const triggers = useSheetTriggers({ focusRef: linkRef, enabled: meId !== null, open: actions !== null, onOpen: setActions })
  const classes = [
    'conversation-row',
    active ? 'active' : '',
    channel.unread ? 'has-unread' : '',
    channel.archivedAt ? 'archived' : '',
  ]
    .filter(Boolean)
    .join(' ')

  const avatar =
    channel.type === 'dm' || bot ? (
      <Avatar user={partnerId ? users.get(partnerId) : null} id={partnerId ?? channel.id} name={name} online={online} />
    ) : (
      <Avatar name={name} id={channel.id} variant={channel.type === 'channel' ? 'channel' : 'person'} />
    )

  return (
    <div className={actions ? 'conversation-item is-held' : 'conversation-item'} data-channel-id={channel.id} {...triggers.rowProps}>
      <Link
        ref={linkRef}
        to={`/c/${channel.id}`}
        className={classes}
        aria-current={active ? 'true' : undefined}
        aria-label={`${name}, ${typeLabel}${managed ? ', managed' : ''}${restricted ? ', only some people can post' : ''}${channel.unread ? `, ${channel.unread} unread messages` : ''}${quietText ? `, ${quietText.toLowerCase()}` : ''}${channel.archivedAt ? ', archived' : ''}`}
      >
        {avatar}
        <span className="conversation-copy">
          <span className="conversation-title-line">
            <span className="conversation-name">
              <span>{name}</span>
              {bot ? <BotTag /> : null}
              {restricted ? (
                <span className="restricted-mark" role="img" aria-label="Only some people can post" title="Only some people can post">
                  <Icon name="megaphone" />
                </span>
              ) : null}
              {managed ? <Icon name="idCard" className="managed-icon" /> : null}
              {channel.archivedAt ? <Icon name="archive" /> : null}
            </span>
            <time className="conversation-time" dateTime={lastActivity(channel)}>
              {formatRelativeTime(lastActivity(channel))}
            </time>
          </span>
          <span className="conversation-preview-line">
            <span className="conversation-preview">{previewText(channel, users, meId)}</span>
            {/* Beside the count, where the row keeps its status: "3 unread, and you won't be told". */}
            {quietText ? (
              <span className="quiet-mark" role="img" aria-label={quietText} title={quietText}>
                <Icon name="mute" />
              </span>
            ) : null}
            {channel.unread ? <span className={quietText ? 'badge quiet' : 'badge'}>{channel.unread > 99 ? '99+' : channel.unread}</span> : null}
          </span>
        </span>
      </Link>
      {meId ? (
        <>
          {/* Shown on hover and focus; on touch screens holding the row replaces it, and screen readers and keyboards keep it. */}
          <button
            type="button"
            className="conversation-actions-button"
            aria-label={`Conversation actions for ${name}`}
            title="Conversation actions"
            aria-haspopup="dialog"
            aria-expanded={actions !== null}
            onClick={(event) => triggers.openFrom(event.currentTarget)}
          >
            <Icon name="more" />
          </button>
          {actions ? (
            <ConversationActions channel={channel} name={name} meId={meId} users={users} avatar={avatar} request={actions} onClose={() => setActions(null)} />
          ) : null}
        </>
      ) : null}
    </div>
  )
}
